import { useEffect, useState } from 'react';
import { readFreshSelection, SCHOOL_SELECTION_EVENT } from '../services/schoolSelectionTtl';

const STORAGE_KEYS = {
  clsdk: 'clsdk_school_selection',
  clsse: 'clsse_school_selection',
  abcse: 'abcse_school_selection',
  clsfi: 'clsfi_school_selection',
};

/**
 * Read the saved (school, class) selection for a given brand slug — same
 * localStorage entry the SchoolSelector wrappers write to.  Re-emits on the
 * `storage` event so cross-tab updates flow through. Either field may be
 * null if the customer hasn't picked one yet.
 *
 * @returns {{ schoolSlug: string|null, className: string|null }}
 */
export default function useSchoolSelection(brandSlug) {
  const key = STORAGE_KEYS[brandSlug] || null;
  const [sel, setSel] = useState(() => read(key));

  useEffect(() => {
    setSel(read(key));
    if (!key) return undefined;
    const onStorage = (e) => {
      if (e.key === key) setSel(read(key));
    };
    // Same-tab writes don't fire `storage` — the selector popup announces
    // them via SCHOOL_SELECTION_EVENT instead.
    const onLocal = (e) => {
      if (!e.detail?.key || e.detail.key === key) setSel(read(key));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SCHOOL_SELECTION_EVENT, onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SCHOOL_SELECTION_EVENT, onLocal);
    };
  }, [key]);

  return sel;
}

function read(key) {
  const empty = { schoolSlug: null, className: null, shopSlug: null };
  if (!key) return empty;
  // readFreshSelection enforces the 24h TTL (and clears expired/legacy entries).
  const parsed = readFreshSelection(key);
  if (!parsed) return empty;
  return {
    schoolSlug: parsed?.school?.slug || null,
    className:  parsed?.class || null,
    shopSlug:   parsed?.season?.slug || null,
  };
}
